import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { insights } from '@repletoryreach/sdk-api';
import { Button, Group, Stack } from '@repletoryreach/sdk-ui';
import { Alert, PageHeader } from '@repletoryreach/sdk-ui/data-display';
import { HelpButton } from '../../app/guides/HelpButton';
import { useT } from '../../i18n/I18nProvider';
import { INSIGHTS_KEY, insightErrorText, isRunning } from './lib/data';
import { NewReportModal } from './NewReportModal';
import { ReportsList } from './ReportsList';
import { RunningCard } from './RunningCard';
import { SettingsCard } from './SettingsCard';
import './insights.css';

/**
 * Chat insights (owner, 29 October 2026: "analyse our last X days of the chats and let the bot analyse the trends and
 * make a report"). New report reads the chats of a period with AI and writes a report the business opens here: what
 * customers asked about, where chats stopped, what went unanswered, the next document to add, and advice. Owners and
 * admins only: reports quote customers and spend credits.
 */
export default function InsightsPage() {
  const t = useT();
  const [creating, setCreating] = useState(false);
  const list = useQuery({
    queryKey: [INSIGHTS_KEY, 'list'],
    queryFn: () => insights.listInsightReports(50),
    refetchInterval: (q) => ((q.state.data?.data ?? []).some(isRunning) ? 3000 : false)
  });
  const reports = list.data?.data ?? [];
  const running = reports.filter(isRunning);

  return (
    <Stack gap="lg" className="rr-insights">
      <PageHeader title={t('insights.title')} description={t('insights.description')} withDivider={false}
        actions={(
          <Group gap="sm">
            <HelpButton guide="insights" />
            <Button onClick={() => setCreating(true)} disabled={running.length > 0} data-guide="insights-new" data-testid="insights-new-report">
              {t('insights.new.button')}
            </Button>
          </Group>
        )} />
      {list.error && <Alert color="error" role="alert">{insightErrorText(t, list.error)}</Alert>}
      {running.map((r) => <RunningCard key={r.id} report={r} />)}
      <div data-guide="insights-list">
        <ReportsList reports={reports} loading={list.isPending} />
      </div>
      <SettingsCard />
      <NewReportModal opened={creating} onClose={() => setCreating(false)} />
    </Stack>
  );
}
